import { UnitOfMeasureEnum } from '../../enums/recipe-enums';
import type {
  IngredientSummaryProps,
  RecipeStepProps,
  RecipeMetaSectionProps,
} from '@repo/ui/src/types';
import type { RecipeTimeProps } from '@views/RecipeDetailPage/_RecipeHero';

const escapeHtml = (str: string): string =>
  str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const htmlHead = `
  <head>
    <link rel="preconnect" href="https://fonts.gstatic.com" />
    <link href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500&display=swap" rel="stylesheet" />
    <style>
      body { margin: 0 auto; }
      #printed-recipe { display: flex; flex-direction: column; align-items: center; justify-content: center; margin: 0 auto; padding: 0px; width: 670px; height: 900px; clear: both; page-break-after: always; font-family: "Roboto", Arial, Helvetica, sans-serif; font-size: 10pt; }
      #printed-recipe .recipe-wrapper { display: flex; flex-direction: column; align-items: center; justify-content: center; }
      #printed-recipe header { height: 85px; text-align: right; }
      #printed-recipe header svg { float: left; width: 150px; }
      #printed-recipe header p { padding-top: 30px; }
      #traeger-logo { height: 85px; width: 85px; }
      #printed-recipe h1 { text-align: center; font-family: "Roboto", Arial, Helvetica, sans-serif; margin-bottom: 0px; font-weight: bold; }
      #printed-recipe h2 { font-family: "Roboto", Arial, Helvetica, sans-serif; font-weight: bold; }
      #printed-recipe h3 { font-family: "Roboto", Arial, Helvetica, sans-serif; font-weight: bold; margin-top: 10px; }
      #printed-recipe .recipe-details { margin-top: 40px; height: 20px; }
      #printed-recipe .recipe-details div { width: 33%; height: 20px; float: left; text-align: center; }
      #printed-recipe .title-section h2 { text-align: center; }
      #printed-recipe .recipe-body { margin-top: 20px; }
      #printed-recipe .recipe-body .recipe-ingredients { width: 37%; float: left; }
      #printed-recipe .recipe-body .recipe-ingredients .ingredient-group { margin-top: 12px; margin-bottom: 8px; font-family: "Roboto", Arial, Helvetica, sans-serif; font-size: 12pt; text-transform: capitalize; align-self: flex-start; }
      #printed-recipe .recipe-body .recipe-ingredients ul, #printed-recipe .recipe-body .recipe-ingredients ul li { margin: 0px; list-style: none; list-style-type: none; padding: 0px; }
      #printed-recipe .recipe-body .recipe-ingredients ul li { margin-bottom: 15px; text-transform: capitalize; }
      #printed-recipe .recipe-body .recipe-instructions { width: 60%; float: right; }
      #printed-recipe .recipe-body .recipe-instructions ol { padding-left: 20px; }
      #printed-recipe .recipe-body .recipe-instructions ol li { margin-bottom: 15px; }
    </style>
  </head>
`;

export interface BuildRecipeToPrintProps {
  recipeMetaSection: RecipeMetaSectionProps;
  ingredientSummary?: IngredientSummaryProps;
  steps?: Array<RecipeStepProps>;
  prepTime: Array<RecipeTimeProps>;
  authorName: string;
  cookTime: Array<RecipeTimeProps>;
  servingSize: number;
  unitOfMeasure: number;
}

const buildSteps = (steps: Array<RecipeStepProps>): string => {
  const list = steps.map((step) => `<li>${escapeHtml(step.instruction ?? '')}</li>`).join('');
  return `
    <div class="recipe-instructions">
      <h2>Steps</h2>
      <ol>${list}</ol>
    </div>
  `;
};

const buildTime = (time: Array<RecipeTimeProps>): string =>
  time.map(({ value, label }) => (label ? `<strong>${escapeHtml(String(value))}&nbsp;${escapeHtml(label)}</strong>` : '')).join('');

const buildIngredients = (ingredientSummary: IngredientSummaryProps, unitOfMeasure: number): string => {
  const groups = ingredientSummary.groups.map(
    (group) => `
    ${group.groupName ? `<h4 class="ingredient-group">${escapeHtml(group.groupName)}</h4>` : ''}
    <ul>${group.ingredients
      .map(
        (ingredient: any) => `
      <li>
        ${escapeHtml((unitOfMeasure === UnitOfMeasureEnum.Imperial ? ingredient.imperial?.measurementDisplay : ingredient.metric?.measurementDisplay) || '')}
        ${escapeHtml((unitOfMeasure === UnitOfMeasureEnum.Imperial ? ingredient.unitOfMeasure : ingredient.metric?.metricUnitOfMeasure) || '')}
        ${escapeHtml(ingredient.ingredient?.name || '')}
      </li>`,
      )
      .join('')}</ul>`,
  );
  return `
    <div class="recipe-ingredients">
      <h2>Ingredients</h2>
      <ul>${groups.join('')}</ul>
    </div>
  `;
};

const buildRecipeToPrint = ({
  recipeMetaSection,
  ingredientSummary,
  steps,
  prepTime,
  authorName,
  cookTime,
  servingSize,
  unitOfMeasure,
}: BuildRecipeToPrintProps): string => `
  <!DOCTYPE html>
  <html>
    ${htmlHead}
    <body>
      <div id="printed-recipe">
        <div id="recipe-wrapper">
          <header id="print-header">
            <p>Download Traeger App for free recipes</p>
          </header>
          <div class="title-section">
            <h1>${escapeHtml(recipeMetaSection.title ?? '')}</h1>
            <h2>${escapeHtml(authorName)}</h2>
          </div>
          <div class="recipe-details">
            <div><strong>Prep Time:</strong> ${buildTime(prepTime)}</div>
            <div><strong>Cook Time:</strong> ${buildTime(cookTime)}</div>
            <div><strong>Serves:</strong> ${servingSize} people</div>
          </div>
          <div class="recipe-body">
            ${ingredientSummary ? buildIngredients(ingredientSummary, unitOfMeasure) : ''}
            ${steps ? buildSteps(steps) : ''}
          </div>
        </div>
      </div>
    </body>
  </html>
`;

export default buildRecipeToPrint;
